import React, { useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import useIdentities from '../../hooks/api/useIdentities'
import CustomFieldFilter from './CustomFieldFilter'
import useGetTaskTypes from '../../hooks/api/useGetTaskTypes'
import { OUTCOMES, TASK_TYPE_DROPDOWN_MAP } from '../../constants'
import useDropdowns from '../../hooks/api/useDropdowns'

const FilterSelection = ({
  filters,
  setFilters,
  data,
  customFields,
  nextSteps,
  types,
}) => {
  const [repQueryString, setRepQueryString] = useState('')

  const handleFilterChange = (key, value) => {
    let newVal = { ...filters, [key]: value }

    if (!value && value !== 0) delete newVal[key]

    setFilters(newVal)
  }

  const clearFilters = () => {
    setFilters({})
  }

  const { identities } = useIdentities(repQueryString, {
    limit: 10,
    is_claimed: true,
  })

  const { taskTypes, loading: taskTypesLoading } = useGetTaskTypes()

  const { dropdownsSummary } = useDropdowns()

  const hasFilters =
    Object.keys(filters).length > 0 &&
    Object.keys(filters).some((key) => !!filters[key])

  const dropdownKey = TASK_TYPE_DROPDOWN_MAP[filters.taskTypeId]

  const taskOutcomes =
    dropdownsSummary.find((ds) => ds.key === dropdownKey)?.options || []

  return (
    <Box sx={{ height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.5,
        }}
      >
        <Typography>
          <b>Select filters</b>
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          mx: -3,
          px: 3,
          mb: 1,
        }}
      >
        <Typography>
          {data.length} result{data.length === 1 ? '' : 's'}
        </Typography>

        {hasFilters && (
          <Button variant={'outlined'} onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </Box>
      <Box
        sx={{
          height: 'calc(100% - 87px)',
          boxShadow:
            '0px 8px 6px -7px rgb(0 0 0 / 40%) inset, 0px -8px 6px -7px rgb(0 0 0 / 40%) inset',
          mx: -3,
          py: '3px',
        }}
      >
        <Box
          sx={{
            overflow: 'scroll',
            height: '100%',
          }}
        >
          <Box sx={{ px: 3, py: 1 }}>
            {nextSteps?.options?.length > 0 && (
              <TextField
                margin={'dense'}
                fullWidth
                select
                label={'Next Step'}
                value={filters.nextStepTypeId || ''}
                onChange={(e) =>
                  handleFilterChange('nextStepTypeId', e.target.value)
                }
              >
                <MenuItem value={null}>All</MenuItem>
                {nextSteps.options.map((ns) => (
                  <MenuItem key={ns.name} value={ns.id}>
                    {ns.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              value={filters.importanceTypeId || ''}
              onChange={(e) =>
                handleFilterChange('importanceTypeId', e.target.value)
              }
              margin={'dense'}
              fullWidth
              select
              label={'Importance'}
            >
              <MenuItem value={null}>All</MenuItem>
              {types.importanceTypes.map((it) => (
                <MenuItem key={it.id} value={it.id}>
                  {it.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              margin={'dense'}
              fullWidth
              label={'Account Name'}
              value={filters.name || ''}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    {'(Includes)'}
                  </InputAdornment>
                ),
              }}
            ></TextField>

            <Autocomplete
              multiple
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={filters.reps || []}
              onChange={(_, value) => {
                handleFilterChange('reps', value)
              }}
              fullWidth
              options={identities}
              getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin={'dense'}
                  fullWidth
                  label='Reps'
                  onChange={(e) => setRepQueryString(e.target.value)}
                ></TextField>
              )}
            />

            <TextField
              margin='normal'
              fullWidth
              label={'Days to Close'}
              value={filters.daysToClose || ''}
              onChange={(e) =>
                handleFilterChange('daysToClose', e.target.value)
              }
              select
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end' sx={{ mr: 2 }}>
                    {'days'}
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value={null}>All</MenuItem>
              {['30', '60', '90+'].map((d, i) => (
                <MenuItem key={i} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>

            <FormControl>
              <FormHelperText>Percent to Close</FormHelperText>

              <Box sx={{ display: 'flex' }}>
                <TextField
                  InputProps={{
                    min: 0,
                    max: 100,
                  }}
                  type={'number'}
                  value={filters.minPercentToClose || ''}
                  onChange={(e) =>
                    handleFilterChange('minPercentToClose', e.target.value)
                  }
                  label={'Min'}
                ></TextField>
                <TextField
                  InputProps={{
                    min: 0,
                    max: 100,
                  }}
                  type={'number'}
                  value={filters.maxPercentToClose || ''}
                  onChange={(e) =>
                    handleFilterChange('maxPercentToClose', e.target.value)
                  }
                  label={'Max'}
                ></TextField>
              </Box>
            </FormControl>
            <FormControl>
              <FormHelperText>Potential Revenue</FormHelperText>

              <Box sx={{ display: 'flex' }}>
                <TextField
                  type={'number'}
                  value={filters.minPotentialRevenue || ''}
                  onChange={(e) =>
                    handleFilterChange('minPotentialRevenue', e.target.value)
                  }
                  label={'Min'}
                ></TextField>
                <TextField
                  type={'number'}
                  value={filters.maxPotentialRevenue || ''}
                  onChange={(e) =>
                    handleFilterChange('maxPotentialRevenue', e.target.value)
                  }
                  label={'Max'}
                ></TextField>
              </Box>
            </FormControl>
            <FormControl>
              <FormHelperText>Closed Revenue</FormHelperText>

              <Box sx={{ display: 'flex' }}>
                <TextField
                  type={'number'}
                  value={filters.minClosedRevenue || ''}
                  onChange={(e) =>
                    handleFilterChange('minClosedRevenue', e.target.value)
                  }
                  label={'Min'}
                ></TextField>
                <TextField
                  type={'number'}
                  value={filters.maxClosedRevenue || ''}
                  onChange={(e) =>
                    handleFilterChange('maxClosedRevenue', e.target.value)
                  }
                  label={'Max'}
                ></TextField>
              </Box>
            </FormControl>

            <TextField
              margin='normal'
              fullWidth
              label={'Outcome'}
              value={filters.outcome || ''}
              onChange={(e) => handleFilterChange('outcome', e.target.value)}
              select
            >
              <MenuItem value={null}>All</MenuItem>

              {['Won', 'Lost', 'Open'].map((d, i) => (
                <MenuItem key={i} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>

            <FormControl sx={{ width: '100%' }}>
              <FormHelperText sx={{ mb: 1 }}>
                Opportunity Created Date
              </FormHelperText>

              <Box sx={{ display: 'flex' }}>
                <TextField
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{ mr: 1 }}
                  type={'date'}
                  value={filters.minOpportunityCreatedDate || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'minOpportunityCreatedDate',
                      e.target.value
                    )
                  }
                  label={'Min'}
                ></TextField>
                <TextField
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type={'date'}
                  value={filters.maxOpportunityCreatedDate || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'maxOpportunityCreatedDate',
                      e.target.value
                    )
                  }
                  label={'Max'}
                ></TextField>
              </Box>
            </FormControl>
            <FormControl sx={{ width: '100%' }}>
              <FormHelperText sx={{ mb: 1 }}>
                Opportunity Closed Date
              </FormHelperText>

              <Box sx={{ display: 'flex' }}>
                <TextField
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{ mr: 1 }}
                  type={'date'}
                  value={filters.minClosedDate || ''}
                  onChange={(e) =>
                    handleFilterChange('minClosedDate', e.target.value)
                  }
                  label={'Min'}
                ></TextField>
                <TextField
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type={'date'}
                  value={filters.maxClosedDate || ''}
                  onChange={(e) =>
                    handleFilterChange('maxClosedDate', e.target.value)
                  }
                  label={'Max'}
                ></TextField>
              </Box>
            </FormControl>

            {!taskTypesLoading && (
              <>
                <Divider sx={{ my: 2 }} />

                <Typography>Completed Tasks</Typography>
                <TextField
                  margin='normal'
                  fullWidth
                  label={'Task type'}
                  value={filters.taskTypeId || ''}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      taskTypeId: e.target.value,
                      taskOutcomeId: '',
                    })
                  }}
                  select
                >
                  <MenuItem value={null}>All</MenuItem>

                  {taskTypes.map((d, i) => (
                    <MenuItem key={i} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  margin='normal'
                  fullWidth
                  label={'Outcome'}
                  value={filters.taskOutcome || ''}
                  onChange={(e) =>
                    handleFilterChange('taskOutcome', e.target.value)
                  }
                  select
                >
                  <MenuItem value={null}>All</MenuItem>

                  {OUTCOMES.map((d, i) => (
                    <MenuItem key={i} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </TextField>

                {taskOutcomes.length > 0 && filters.taskTypeId && (
                  <TextField
                    margin='normal'
                    fullWidth
                    label={'Task outcome'}
                    value={filters.taskOutcomeId || ''}
                    onChange={(e) =>
                      handleFilterChange('taskOutcomeId', e.target.value)
                    }
                    select
                  >
                    <MenuItem value={null}>All</MenuItem>

                    {taskOutcomes.map((d, i) => (
                      <MenuItem key={i} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {filters.taskTypeId && (
                  <FormControl sx={{ width: '100%' }}>
                    <FormHelperText sx={{ mb: 1 }}>Task Date</FormHelperText>

                    <Box sx={{ display: 'flex' }}>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        sx={{ mr: 1 }}
                        type={'date'}
                        value={filters.minTaskDate || ''}
                        onChange={(e) =>
                          handleFilterChange('minTaskDate', e.target.value)
                        }
                        label={'Min'}
                      ></TextField>
                      <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        type={'date'}
                        value={filters.maxTaskDate || ''}
                        onChange={(e) =>
                          handleFilterChange('maxTaskDate', e.target.value)
                        }
                        label={'Max'}
                      ></TextField>
                    </Box>
                  </FormControl>
                )}
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography>Custom Fields</Typography>

            {customFields.map((c) => (
              <CustomFieldFilter
                key={c.id}
                filters={filters}
                handleFilterChange={handleFilterChange}
                customField={c}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default FilterSelection
